import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import customParseFormat from "dayjs/plugin/customParseFormat"
import relativeTime from "dayjs/plugin/relativeTime"
import { SERVER_TIMEZONE } from '../config/consts'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

export const userTimeZone = dayjs.tz.guess()
export const serverTimeZone = SERVER_TIMEZONE
export const businessTimeZone = SERVER_TIMEZONE
export const displayTimeZone = userTimeZone

// ============================================
// TIMEZONE-AWARE DATE CREATION
// Use these instead of dayjs(new Date) or new Date()
// ============================================

/**
 * Current moment in user's timezone
 * Use instead of: dayjs(new Date), dayjs(), new Date()
 */
export const now = () => dayjs().tz(userTimeZone)

/**
 * Start of today in user's timezone
 */
export const today = () => dayjs().tz(userTimeZone).startOf('day')

/**
 * Start of current month in user's timezone
 */
export const startOfMonth = () => dayjs().tz(userTimeZone).startOf('month')

/**
 * End of current month in user's timezone
 */
export const endOfMonth = () => dayjs().tz(userTimeZone).endOf('month')

// ============================================
// FOR SENDING TO SERVER
// ============================================

/**
 * Convert date to YYYY-MM-DD string for API
 * Use for: dateOfBirth, dueDate, startDate, etc. (date-only fields)
 *
 * @param {Date|dayjs} date - Date from picker or state
 * @returns {string|null} - "2024-01-15" format, or null if invalid
 */
export const dateToServer = (date) => {
  if (!date) return null
  // Extract date parts to avoid timezone issues
  const d = dayjs(date)
  // Check if the date is valid before formatting
  if (!d.isValid()) return null
  return d.format('YYYY-MM-DD')
}

export const isRealDate = (value) => Boolean(value) && dayjs(value).isValid()

/**
 * Convert datetime to ISO string for API
 * Use for: timestamps that need time component
 *
 * @param {Date|dayjs} date
 * @returns {string|null} - ISO 8601 UTC string
 */
export const dateTimeToServer = (date) => {
  if (!date) return null
  return dayjs(date).utc().toISOString()
}

// ============================================
// FOR RECEIVING FROM SERVER
// ============================================

/**
 * Convert UTC date from server to dayjs object in user's timezone
 * Use when you need to work with the date (e.g., set in DatePicker)
 *
 * @param {string} isoDate - ISO date string from API
 * @returns {dayjs|null}
 */
export const serverToLocal = (isoDate) => {
  if (!isoDate) return null
  return dayjs.utc(isoDate).tz(userTimeZone)
}

/**
 * Convert UTC date from server to native Date object
 * Use when DatePicker requires native Date
 *
 * @param {string} isoDate - ISO date string from API
 * @returns {Date|null}
 */
export const serverToLocalDate = (isoDate) => {
  if (!isoDate) return null
  return dayjs.utc(isoDate).tz(userTimeZone).toDate()
}

// ============================================
// EXISTING UTILITY FUNCTIONS
// ============================================


export function extractDate(dateTimeString) {
  const dateWithTime = dayjs(dateTimeString).tz(userTimeZone).toDate()
  const monthNames = [
    "January", "February", "March",
    "April", "May", "June",
    "July", "August", "September",
    "October", "November", "December"
  ]

  return `${monthNames[dateWithTime.getMonth()]} ${dateWithTime.getDate()}, ${dateWithTime.getFullYear()}`
}

export function extractDateTime(dateTimeString, longMonths = true) {
  const dateTime = dayjs(dateTimeString).tz(userTimeZone).toDate()
  const monthNames = longMonths
    ? [
      "January", "February", "March",
      "April", "May", "June",
      "July", "August", "September",
      "October", "November", "December"
    ]
    : [
      "Jan", "Feb", "Mar",
      "Apr", "May", "Jun",
      "Jul", "Aug", "Sep",
      "Oct", "Nov", "Dec"
    ]

  const hours = dateTime.getHours() % 12 || 12
  const amPm = dateTime.getHours() >= 12 ? "PM" : "AM"
  const minutes = dateTime.getMinutes().toString().padStart(2, "0")

  return `${monthNames[dateTime.getMonth()]} ${dateTime.getDate()}, ${dateTime.getFullYear()}, ${hours}:${minutes} ${amPm}`
}

export function formatDate(inputDate) {
  const date = dayjs(inputDate).tz(userTimeZone).toDate()

  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true
  }

  return new Intl.DateTimeFormat("en-US", options).format(date)
}

export function extractShortDate(dateTimeString) {
  const dateWithTime = dayjs(dateTimeString).tz(userTimeZone).toDate()
  const monthNames = [
    "Jan", "Feb", "Mar",
    "Apr", "May", "Jun",
    "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec"
  ]

  const month = monthNames[dateWithTime.getMonth()]
  const day = dateWithTime.getDate()

  return `${month} ${day}`
}

export function extractTime(dateTimeString) {
  const dateTime = dayjs(dateTimeString).tz(userTimeZone).toDate()
  const hours = dateTime.getHours() % 12 || 12
  const amPm = dateTime.getHours() >= 12 ? "PM" : "AM"
  const minutes = dateTime.getMinutes().toString().padStart(2, "0")

  return `${hours}:${minutes} ${amPm}`
}

export function formatFullDate(inputDate) {
  const date = dayjs(inputDate).tz(userTimeZone).toDate()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

export function formatFullDateUTC(inputDate) {
  const date = dayjs.utc(inputDate).toDate()
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0")
  const day = date.getUTCDate().toString().padStart(2, "0")
  const year = date.getUTCFullYear()

  return `${month}/${day}/${year}`
}

/**
 * Format date-only field for display
 * Uses UTC to extract the date portion, which works correctly for:
 * - Old data stored as midnight UTC (e.g., 1990-01-23T00:00:00.000Z)
 * - New data stored with timezone offset (e.g., 1990-01-23T05:00:00.000Z for EST)
 *
 * @param {string} isoDate - ISO date string from API
 * @returns {string} Formatted date string (e.g., "01/23/1990") or "-" if no date
 */
export function formatDateOnlyField(isoDate) {
  if (!isoDate) return '-'
  return dayjs.utc(isoDate).format('MM/DD/YYYY')
}

/**
 * Format a date-only field for display (smart version)
 * Handles both legacy data (UTC midnight) and new data (timezone-aware)
 *
 * For date-only fields like enrollmentDate, dateOfBirth:
 * - Old format: 2026-01-22T00:00:00.000Z (UTC midnight)
 * - New format: 2026-01-22T05:00:00.000Z (NY midnight = 05:00 UTC)
 *
 * This function extracts the intended date regardless of how it was stored.
 *
 * @param {string} isoDate - ISO date string from API
 * @param {string} format - Output format (default: 'MM/DD/YYYY')
 * @returns {string} Formatted date string or '-' if no date
 */
export function formatDateField(isoDate, format = 'MM/DD/YYYY') {
  if (!isoDate) return '-'

  // Parse the date and check if it's likely a date-only value
  // Date-only values are stored at midnight (00:00:00) in some timezone
  const date = dayjs.utc(isoDate)

  // Get the hours in UTC - if 0-6, it's likely a date-only field
  // (00:00 UTC or 04:00-05:00 UTC for EST/EDT midnight)
  const utcHours = date.hour()

  if (utcHours === 0) {
    // Old format: stored as UTC midnight - use UTC date directly
    return date.format(format)
  } else if (utcHours >= 4 && utcHours <= 6) {
    // New format: stored as EST/EDT midnight (04:00-05:00 UTC)
    // Convert to NY timezone to get the intended date
    return date.tz(SERVER_TIMEZONE).format(format)
  } else {
    // Likely a full timestamp, convert to local timezone
    return date.tz(userTimeZone).format(format)
  }
}

/**
 * Convert UTC date-only field from server to dayjs object for DatePicker
 * Returns UTC dayjs object to preserve the date portion correctly
 *
 * This should be used for date-only fields like dateOfBirth when loading into DatePicker
 *
 * @param {string} isoDate - ISO date string from API (e.g., "2024-01-23T00:00:00.000Z")
 * @returns {dayjs|null}
 */
export const serverToLocalDateOnly = (isoDate) => {
  if (!isoDate) return null
  return dayjs.utc(isoDate)
}

export const getNextWorkingDay = (date) => {
  let newDate = dayjs(date).add(1, "day")
  const dayOfWeek = newDate.day()

  if (dayOfWeek === 6) {
    newDate = newDate.add(2, "day")
  } else if (dayOfWeek === 0) {
    newDate = newDate.add(1, "day")
  }

  return newDate
}

export const getSlashDateFormat = (date, changeTimeZone = true) => {
  return changeTimeZone ? dayjs(date).tz(userTimeZone).format('MM/DD/YYYY') : dayjs(date).format('MM/DD/YYYY')
}

export const convertLocalToServerTime = (date) => {
  if (!date) return ''

  return dayjs(date).utc().toISOString()
}
// Renders in BUSINESS time, not the viewer's browser timezone. Its three callers all show
// payment dates (Transactions tab, payment-method rows, the contact Payment tab), and a
// payment date is a business fact — it must not change depending on who is looking at it.
//
// With userTimeZone, any viewer behind business time read the previous day for values stored
// at business-day midnight, which is how the same payment showed 07/21 on the Payment Plan
// and 07/20 on Transactions. Matches the project rule: use businessTimeZone, never
// userTimeZone, for server-derived dates.
//
// Name kept as-is to avoid churning the call sites; "Local" now means business-local.
export const convertServerToLocalTime = (date, format = 'MM/DD/YYYY') => {
  if (!date) return ''
  return dayjs.utc(date).tz(businessTimeZone).format(format)
}

/**
 * Format a server date as a date-only BUSINESS date (America/New_York), never the viewer's
 * browser timezone. A payment / settlement / due date is a business fact — it must read the
 * same for staff in New York, Los Angeles, or Kyiv.
 *
 * Legacy-safe (same intent as formatDateField, but pinned to business time instead of the
 * viewer's zone for real timestamps):
 * - Legacy date-only values stored at UTC midnight (00:00:00Z) keep their UTC calendar date,
 *   so they never slip to the previous day when projected into Eastern time.
 * - NY-midnight values (04:00–05:00Z) and real timestamps render the Eastern calendar day.
 *
 * @param {string} value - ISO date string from API
 * @param {string} format - Output format (default: 'MM/DD/YYYY')
 * @returns {string} Formatted date, or '' if no value
 */
export function formatBusinessDate(value, format = 'MM/DD/YYYY') {
  if (!value) return ''
  const date = dayjs.utc(value)
  // Legacy date-only values were stored at exactly UTC midnight; keep their UTC date so they
  // do not drift a day when projected into Eastern time.
  if (date.hour() === 0 && date.minute() === 0 && date.second() === 0) {
    return date.format(format)
  }
  return date.tz(businessTimeZone).format(format)
}

/**
 * Format a server timestamp in BUSINESS time (America/New_York) WITH a clock, labelled ` ET`
 * so the reader knows the zone. Use for audit / event timestamps that are business facts
 * (created / approved / processed), so they read identically regardless of viewer location.
 *
 * @param {string} value - ISO date string from API
 * @param {string} format - Output format (default: 'MM/DD/YYYY h:mm A')
 * @returns {string} e.g. '07/21/2026 3:42 PM ET', or '' if no value
 */
export function formatBusinessDateTime(value, format = 'MM/DD/YYYY h:mm A') {
  if (!value) return ''
  return `${dayjs.utc(value).tz(businessTimeZone).format(format)} ET`
}

export const timeAgo = (value) => (value ? dayjs(value).fromNow() : '')

// ============================================
// MIGRATION GUIDE
// ============================================
// Replace:
//   dayjs(new Date)     → now()
//   dayjs().startOf()   → today() or startOfMonth()
//   new Date()          → now().toDate()
//   value.toISOString() → dateTimeToServer(value)
//   value               → dateToServer(value) // for date-only fields
// ============================================
